import xor from 'lodash/xor';
import {
  FC,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
  useLayoutEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Resource, PlaylistItem, Content } from '@raydiant/api-client-js';
import Page from '../../components/Page';
import ReadResourceWarning from '../../components/ReadResourceWarning';
import {
  selectUserProfile,
  selectIsEnterpriseUser,
} from '../../selectors/user';
import {
  selectSearchedLibraryByOwner,
  selectFoldersById,
} from '../../selectors/v2/folders';
import { selectPlaylistsById } from '../../selectors/v2/playlists';
import { selectPresentationsById } from '../../selectors/v2/presentations';
import * as folderActions from '../../actions/folders';
import * as presentationActions from '../../actions/presentations';
import {
  sortFolder,
  isResourceDeleted,
  createNodeId,
  parseNodeId,
  canReadResource,
  createDefaultPlaylist,
  hasRoleOrHigher,
} from '../../utilities';
import { createNewId } from '../../utilities/identifiers';
import * as paths from '../../routes/paths';
import {
  selectSortOptions,
  selectSelectedItems,
  selectSearchQuery,
  selectLayoutOptions,
} from './selectors';
import * as actions from './actions';
import Dialog, {
  DialogBody,
  DialogHeader,
  DialogHeading,
} from '@raydiant/raydial/components/Dialog';
import AddContentActionButton from './AddContentActionButton';
import AddContentActionBarV2 from './AddContentActionBarV2';
import Select, { SelectItem } from '@raydiant/raydial/components/Select';
import { useLibrary } from '../../hooks';
import Button from '@raydiant/raydial/components/Button';
import Text from '@raydiant/raydial/components/Text';
import Spinner from '@raydiant/raydial/components/Spinner';
import { BACK_BUTTON_ICON } from '../LocationsPage/DeviceShare/DeviceShare';
import AddContentPageContentCard from './AddContentContentCard';
import AddContentPageEmptyLibrary from './AddContentPageEmptyLibrary';
import AddContentPageListView from './AddContentPageListView';
import DomainProfilesLayout from './DomainProfilesLayout';
import {
  changeV3DomainProfilesToV4,
  getSortedLibraryData,
} from '../LibraryPage/utils';
import { selectOtherDomainProfiles } from '../../selectors/v2/domains';

interface AddContentPageProps {
  isModal: boolean;
}

export const DEFAULT_QUERY = {
  scope: 'owned',
  profile_id: '',
  folder_id: '',
  search: '',
  sort_by: '',
  sort_direction: '',
  back_to: '',
};

const AddContentPageV2: FC<AddContentPageProps> = ({ isModal }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  // Selectors

  const libraryByOwner = useSelector(selectSearchedLibraryByOwner);
  const foldersById = useSelector(selectFoldersById);
  const sortOptions = useSelector(selectSortOptions);
  const currentUser = useSelector(selectUserProfile);
  const isEnterpriseUser = useSelector(selectIsEnterpriseUser);
  const selectedItemsBySelectionId = useSelector(selectSelectedItems);
  const playlistsById = useSelector(selectPlaylistsById);
  const presentationsById = useSelector(selectPresentationsById);
  const searchQuery = useSelector(selectSearchQuery);
  const layoutOptions = useSelector(selectLayoutOptions);
  const otherDomainProfiles = useSelector(selectOtherDomainProfiles);

  // State

  const [isAddPopOverOpen, setIsAddPopOverOpen] = useState(false);
  const [isReadResourceWarningOpen, setIsReadResourceWarningOpen] =
    useState(false);
  const [scope, setScope] = useState('owned');

  const queryParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      saveTo: searchParams.get('saveTo') ?? '',
      backTo: searchParams.get('backTo') ?? '',
      selectionId: searchParams.get('selectionId') ?? '',
      addToLabel: searchParams.get('addToLabel') ?? '',
      folderId: searchParams.get('folderId') ?? '',
      profileId: searchParams.get('profileId') ?? '',
      playlistId: searchParams.get('playlistId') ?? '',
      sessionId: searchParams.get('sessionId') ?? undefined,
      createPlaylist: searchParams.get('createPlaylist') === 'true',
    };
  }, [location.search]);

  // Query
  const { data: libraryData, isLoading: libraryLoading } = useLibrary(
    {
      ...DEFAULT_QUERY,
      scope: scope === 'shared' ? 'shared' : 'owned',
      folder_id: queryParams.folderId,
      profile_id: queryParams.profileId,
      search: searchQuery,
    },
    {
      enabled: !(scope === 'users' && !queryParams.profileId),
      staleTime: 0,
    },
  );

  // Memoizers
  const sortedLibraryData = useMemo(
    () => libraryData && getSortedLibraryData(libraryData, sortOptions),
    [libraryData, sortOptions],
  );

  const selectedItems = useMemo(
    () => selectedItemsBySelectionId[queryParams.selectionId] || [],
    [selectedItemsBySelectionId, queryParams.selectionId],
  );

  const selectedNodeIds = useMemo(() => {
    return selectedItems.map(({ id, type }) => createNodeId(type, id));
  }, [selectedItems]);

  const selectedFolder = foldersById[queryParams.folderId];
  const libraryFolder = libraryByOwner[queryParams.profileId];
  const folder = queryParams.folderId ? selectedFolder : libraryFolder;

  const folderItems = useMemo(() => {
    if (!folder) return [];

    return sortFolder(
      folder.presentations.filter(
        (presentation) => !isResourceDeleted(presentation),
      ),
      folder.playlists.filter((playlist) => !isResourceDeleted(playlist)),
      folder.folders.filter((folder) => !isResourceDeleted(folder)),
      sortOptions,
    );
  }, [folder, sortOptions]);

  const itemsToAdd = useMemo<PlaylistItem[]>(() => {
    const items: PlaylistItem[] = [];

    if (queryParams.createPlaylist && currentUser) {
      const playlist = createDefaultPlaylist({
        id: createNewId(),
        name: 'New Empty Playlist',
        resource: { profile: { id: currentUser.id } },
      });
      items.push({
        id: createNewId(),
        presentationId: null,
        playlistId: playlist.id,
        playlist,
      });
    }

    for (const { id, type } of selectedItems) {
      if (type === 'presentation') {
        const presentation = presentationsById[id];
        items.push({
          id: createNewId(),
          presentationId: id,
          playlistId: null,
          presentation,
        });
      } else if (type === 'playlist') {
        const playlist = playlistsById[id];
        items.push({
          id: createNewId(),
          presentationId: null,
          playlistId: id,
          playlist,
        });
      }
    }

    return items;
  }, [
    playlistsById,
    presentationsById,
    selectedItems,
    currentUser,
    queryParams.createPlaylist,
  ]);

  // Callbacks

  const onLibrarySelectionChange = useCallback(
    (key: string) => {
      if (key === scope) return;
      history.push(
        paths.presentations({
          ...queryParams,
          profileId: '',
          folderId: '',
        }),
      );
      setScope(key);
    },
    [history, queryParams, scope],
  );

  const resolveReadResourceWarning = useRef<((result: boolean) => void) | null>(
    null,
  );

  const showReadResourceWarning = async (): Promise<boolean> => {
    return new Promise((resolve) => {
      resolveReadResourceWarning.current = (result: boolean) => {
        resolveReadResourceWarning.current = null;
        setIsReadResourceWarningOpen(false);
        resolve(result);
      };

      setIsReadResourceWarningOpen(true);
    });
  };

  const setSelectedNodeIds = useCallback(
    async (nodeIds: string[]) => {
      const selectedItems = nodeIds.map((nodeId) => {
        const { id, type } = parseNodeId(nodeId);
        return { id, type };
      });

      // Determine if we need to show the read resource warning for
      // any of the selected presentations. Only show the warning once
      // per selection to prevent multiple confirmations when selecting
      // multiple presentations.
      let userConfirmedAdd;

      const parentPlaylist = playlistsById[queryParams.playlistId];
      if (parentPlaylist) {
        for (const { id, type } of selectedItems) {
          let resource: Resource | null = null;
          if (type === 'presentation') {
            const presentation = presentationsById[id];
            if (!presentation) continue;
            resource = presentation.resource;
          } else if (type === 'playlist') {
            const playlist = playlistsById[id];
            if (!playlist) continue;
            resource = playlist.resource;
          }

          if (
            resource &&
            userConfirmedAdd === undefined &&
            !canReadResource(parentPlaylist.resource.profile, resource)
          ) {
            userConfirmedAdd = await showReadResourceWarning();
            if (!userConfirmedAdd) return;
          }
        }
      }

      dispatch(
        actions.setSelectedItems({
          selectionId: queryParams.selectionId,
          items: selectedItems,
        }),
      );
    },
    [
      dispatch,
      queryParams.selectionId,
      queryParams.playlistId,
      presentationsById,
      playlistsById,
    ],
  );

  const toggleNodeId = useCallback(
    (nodeId: string) => {
      setSelectedNodeIds(xor(selectedNodeIds, [nodeId]));
    },
    [setSelectedNodeIds, selectedNodeIds],
  );

  const goBack = useCallback(() => {
    dispatch(
      actions.clearSelectedItems({
        selectionId: queryParams.selectionId,
      }),
    );
    history.push(queryParams.backTo);
  }, [dispatch, history, queryParams]);

  const addPlaylistItems = useCallback(() => {
    dispatch(
      actions.setItemsToAdd({
        selectionId: queryParams.selectionId,
        items: itemsToAdd,
      }),
    );
    history.push(queryParams.saveTo);
  }, [dispatch, history, queryParams, itemsToAdd]);

  const openAppSelector = useCallback(() => {
    history.push(
      paths.applications({
        backTo: paths.presentations(queryParams),
        selectionId: queryParams.selectionId,
        // When clicking "Create Playlist" from the add content flow, instead of opening the builder
        // for a new playlist we want to redirect back to the builder for the top-level playlist with
        // a new nested playlist.
        createPlaylistTo: paths.presentations({
          ...queryParams,
          createPlaylist: true,
        }),
        ...(queryParams.folderId ? { folderId: queryParams.folderId } : {}),
      }),
    );
  }, [history, queryParams]);

  const newPlaylist = useCallback(() => {
    history.push(
      paths.presentations({
        ...queryParams,
        createPlaylist: true,
      }),
    );
  }, [history, queryParams]);

  const onAddPopOverClick = useCallback((value: boolean) => {
    setIsAddPopOverOpen(value);
  }, []);

  const onContentCardClick = useCallback(
    (content: Content) => {
      const isFolder = content.resourceType === 'folder';
      isFolder
        ? history.push(
            paths.presentations({
              ...queryParams,
              folderId: content.id,
            }),
          )
        : toggleNodeId(`${content.resourceType}|${content.id}`);
    },
    [history, queryParams, toggleNodeId],
  );

  const onSelectProfile = useCallback(
    (id: string) => {
      history.push(
        paths.presentations({
          ...queryParams,
          profileId: id,
        }),
      );
    },
    [history, queryParams],
  );

  // Variables
  const v4DomainProfiles = useMemo(
    () => changeV3DomainProfilesToV4(otherDomainProfiles),
    [otherDomainProfiles],
  );

  const folderPaths = sortedLibraryData?.folderPath || [];
  const previousFolderPath = folderPaths[folderPaths.length - 2];
  const previousFolderId = previousFolderPath?.id || '';
  const isLoading = libraryLoading;

  // Side-effects

  // Immediately call addPlaylistItems before rendering if we are creating a new nested playlist.
  // addPlaylistItems will redirect back to the builder page with the new items.
  useLayoutEffect(() => {
    if (!queryParams.createPlaylist) return;
    // Make sure the current user is set before calling addPlaylistItems to ensure we create a new playlist.
    if (!currentUser) return;
    addPlaylistItems();
  }, [queryParams.createPlaylist, addPlaylistItems, currentUser]);

  // Fetch selected folder.
  useEffect(() => {
    if (!queryParams.folderId) return;
    dispatch(folderActions.fetchFolder(queryParams.folderId, {}));
  }, [dispatch, queryParams.folderId]);

  // Poll presentations with file uploads when folder items changes.
  useEffect(() => {
    const presentationIds: string[] = [];

    folderItems.forEach((item) => {
      if (item.presentation) {
        presentationIds.push(item.presentation.id);
      }
    });

    dispatch(
      presentationActions.pollPresentationsWithFileUploads(presentationIds),
    );
  }, [dispatch, folderItems]);

  // Stop polling when unmounting.
  // @ts-ignore
  useEffect(() => {
    return () => dispatch(presentationActions.stopPollingPresentations());
  }, [dispatch]);

  const backToParentFolder = useCallback(() => {
    if (queryParams.folderId) {
      history.push(
        paths.presentations({
          ...queryParams,
          folderId: previousFolderId,
        }),
      );
    }

    if (queryParams.profileId) {
      history.push(
        paths.presentations({
          ...queryParams,
          profileId: '',
        }),
      );
    }
  }, [history, previousFolderId, queryParams]);

  // Render

  const libraryEmpty = sortedLibraryData?.data.length === 0;

  let pageTitle: string;
  if (selectedItems.length === 0) {
    pageTitle = folder && 'id' in folder ? folder.name : 'Select items';
  } else {
    pageTitle = `Selected ${selectedItems.length} ${
      selectedItems.length === 1 ? 'item' : 'items'
    }`;
  }

  let pageBackTo: string = queryParams.backTo;
  if (folder && 'id' in folder) {
    pageBackTo = paths.presentations({
      ...queryParams,
      folderId: folder.resource.parentFolderId || '',
    });
  }

  const pageProps = {
    title: pageTitle,
    backTo: pageBackTo,
    hideNavigation: isModal,
  };

  const hasAccessToUserLibraries =
    currentUser && hasRoleOrHigher('standard', currentUser);

  const renderHeader = () => (
    <>
      <div className="flex items-center justify-between">
        <div className="mb-6">
          <div className="text-black">Libraries</div>
          <div>
            <Text variant="body" className="font-regular text-gray-600">
              {pageTitle}
            </Text>
          </div>
        </div>
        <AddContentActionButton
          isOpen={isAddPopOverOpen}
          onClick={onAddPopOverClick}
          onPresentationClick={() => {
            openAppSelector();
            setIsAddPopOverOpen(false);
          }}
          onPlaylistClick={() => {
            newPlaylist();
            setIsAddPopOverOpen(false);
          }}
          isDisabled={scope !== 'owned'}
        />
      </div>
      <div className="flex flex-col-reverse gap-4 md:flex-row md:gap-6">
        <div className="flex flex-1 items-end">
          <AddContentActionBarV2 sortDisabled={false} />
        </div>
        <div className="w-full md:w-[218px]">
          <Select
            label="Library"
            isDisabled={!isEnterpriseUser}
            className="font-regular"
            selectedKey={scope}
            onSelectionChange={(e) => onLibrarySelectionChange(e as string)}
          >
            <SelectItem id="owned">My Library</SelectItem>
            {isEnterpriseUser && (
              <>
                <SelectItem id="shared">Shared With Me</SelectItem>
                {hasAccessToUserLibraries && (
                  <SelectItem id="users">User Libraries</SelectItem>
                )}
              </>
            )}
          </Select>
        </div>
      </div>
    </>
  );

  const renderFooter = () => (
    <footer className="flex pt-9 mb-4 px-4">
      <div className="w-full flex flex-col gap-2 md:flex-row md:h-14">
        <div className="flex flex-1 text-lg font-medium text-gray-600 justify-center md:justify-start">
          <Text>Select items to add to "New Playlist"</Text>
        </div>
        <div className="flex flex-col gap-4 md:flex-row">
          <Button
            className="md:w-[270px]"
            size="large"
            variant="secondary"
            onPress={goBack}
          >
            Cancel
          </Button>
          <Button
            className="md:w-[270px]"
            size="large"
            variant="primary"
            onPress={addPlaylistItems}
            isDisabled={selectedItems.length === 0}
          >
            Add to Playlist
          </Button>
        </div>
      </div>
    </footer>
  );

  if (isLoading || !currentUser) {
    return (
      <Page {...pageProps}>
        <Dialog className="md:h-[699px]">
          <DialogHeader>
            <DialogHeading>{renderHeader()}</DialogHeading>
          </DialogHeader>
          <DialogBody className="p-4">
            <div className="flex justify-center items-center gap-4 flex-wrap h-[288px] md:min-h-[394px] md:gap-6">
              <Spinner size="large" />
            </div>
          </DialogBody>
          {renderFooter()}
        </Dialog>
      </Page>
    );
  }

  const renderLibrary = () => {
    if (libraryEmpty) {
      return (
        <AddContentPageEmptyLibrary
          pageUrl={queryParams.backTo}
          onCreate={openAppSelector}
        />
      );
    }

    if (layoutOptions === 'grid') {
      return sortedLibraryData?.data.map((content) => {
        const selected = selectedItems.some(
          (item) =>
            item.id === content.id && item.type === content.resourceType,
        );
        return (
          <AddContentPageContentCard
            key={content.resourceId}
            content={content}
            onClick={() => onContentCardClick(content)}
            selected={selected}
            batchMode={content.resourceType !== 'folder'}
            showOwner={scope === 'shared'}
          />
        );
      });
    }
    if (layoutOptions === 'list') {
      return (
        <AddContentPageListView
          contents={sortedLibraryData?.data || []}
          onClick={onContentCardClick}
          selectedItems={selectedItems}
        />
      );
    }
  };

  const getPageTitle = () => {
    const showBackButton = queryParams.profileId || queryParams.folderId;
    let title = '';

    if (queryParams.profileId) {
      const selectedProfile = v4DomainProfiles.find(
        (profile) => profile.id === queryParams.profileId,
      );
      title = `${selectedProfile?.name}'s Library`;
    }

    if (queryParams.folderId) {
      title = folderPaths[folderPaths.length - 1].name || '';
    }

    return (
      <div className="flex items-center mb-4">
        {showBackButton && (
          <button className="p-2" onClick={() => backToParentFolder()}>
            {BACK_BUTTON_ICON}
          </button>
        )}
        <div className="text-2xl font-bold text-black">{title}</div>
      </div>
    );
  };

  const renderPageContent = () => {
    if (scope === 'users' && !queryParams.profileId) {
      return (
        <DomainProfilesLayout
          layout={layoutOptions}
          domainProfiles={v4DomainProfiles}
          onClick={onSelectProfile}
        />
      );
    } else {
      return renderLibrary();
    }
  };

  return (
    <Page {...pageProps}>
      <Dialog className="md:h-[699px] bg-white rounded-3xl">
        <DialogHeader>
          <DialogHeading>{renderHeader()}</DialogHeading>
        </DialogHeader>
        <DialogBody className="p-4">
          {getPageTitle()}
          <div className="flex justify-center gap-4 flex-wrap h-[288px] md:h-auto md:justify-start md:gap-6">
            {renderPageContent()}
          </div>
        </DialogBody>

        {renderFooter()}
      </Dialog>
      <ReadResourceWarning
        ownerResourceType="playlist"
        resourceType="presentation"
        open={isReadResourceWarningOpen}
        onConfirm={resolveReadResourceWarning.current}
      />
    </Page>
  );
};

export default AddContentPageV2;
